import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import dandarLogo from "../../assets/images/dandar-logo.png";
import loginPlaceholder from "../../assets/images/login-placeholder.png";
import { OTPInput } from "./OtpInput";
import { AuthService } from "../../services/api";
import toast from "react-hot-toast";

interface ForgotPasswordProps {
  onBack: () => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onBack }) => {
  const [step, setStep] = useState(1);
  const [mobileNumber, setMobileNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleMobileVerification = async () => {
    if (!mobileNumber || !/^\d{10}$/.test(mobileNumber)) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    }

    setLoading(true);
    const response = await AuthService.sendOTP(mobileNumber);
    setLoading(false);

    if (response.success) {
      toast.success("OTP sent successfully!");
      setStep(2);
    } else {
      toast.error(response.message || "Failed to send OTP.");
    }
  };

  const handleOtpVerification = async () => {
    if (!otp || otp.length !== 4) {
      toast.error("Please enter a valid 4-digit OTP.");
      return;
    }

    setLoading(true);
    const response = await AuthService.verifyOTP(mobileNumber, otp);
    setLoading(false);

    if (response.success) {
      toast.success("OTP verified successfully!");
      setStep(3);
    } else {
      toast.error(response.message || "OTP verification failed.");
    }
  };

  // ✅ Step 3: Update Password
  const handlePasswordReset = async () => {
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match!");
      return;
    }

    setLoading(true);
    const response = await AuthService.updatePassword(
      mobileNumber,
      newPassword
    );
    setLoading(false);

    if (response.success) {
      toast.success("Password successfully reset!");
      setStep(1); // Reset to login page
    } else {
      toast.error(response.message || "Password reset failed.");
    }
  };

  return (
    <div className="min-h-screen flex w-full">
      {/* Left Section - Forgot Password Form */}
      <div className="w-2/5 flex items-center justify-center p-20">
        <div className="w-full max-w-md">
          <img src={dandarLogo} alt="Dandar Health" className="h-20 w-60 mb-8" />
          <div className="space-y-6">
            {/* Step 1: Verify Mobile */}
            {step === 1 && (
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Forgot Password
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Enter your mobile number to reset your password.
                </p>
                <label className="font-semibold text-gray-900">
                  Mobile Number
                </label>
                <input
                  type="tel"
                  value={mobileNumber}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,10}$/.test(value)) {
                      setMobileNumber(value);
                    }
                  }}
                  className="w-full px-3 py-2 mb-5 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="+91XXXXXXXXXX"
                />
                <button
                  onClick={handleMobileVerification}
                  disabled={loading}
                  className="w-full bg-emerald-600 text-white py-2 rounded-md text-sm font-medium hover:bg-emerald-700 transition-colors duration-200"
                >
                  {loading ? "Sending OTP..." : "Send OTP"}
                </button>
                <button
                  onClick={onBack}
                  className="mt-4 text-sm text-emerald-600"
                >
                  ← Remember your password?
                </button>
              </div>
            )}

            {/* Step 2: Verify OTP */}
            {step === 2 && (
              <div className="space-y-5">
                <h1 className="text-2xl font-bold text-gray-900">
                  Forgot Password
                </h1>
                <p className="text-sm text-gray-600">
                  We have sent an OTP to{" "}
                  <span style={{ color: "#148F45" }}>{mobileNumber}</span>.
                </p>
                <OTPInput setOtp={setOtp} />
                <button
                  onClick={handleOtpVerification}
                  disabled={loading}
                  className="w-full bg-emerald-600 text-white py-2 rounded-md text-sm font-medium hover:bg-emerald-700 transition-colors duration-200"
                >
                  {loading ? "Verifying..." : "Verify OTP"}
                </button>
              </div>
            )}

            {/* Step 3: Reset Password */}
            {step === 3 && (
              <div className="space-y-5">
                <h1 className="text-2xl font-bold text-gray-900">
                  Reset Password
                </h1>
                <p className="text-sm text-gray-600">
                  Create and confirm a new password.
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <button
                  onClick={handlePasswordReset}
                  disabled={loading}
                  className="w-full bg-emerald-600 text-white py-2 rounded-md text-sm font-medium hover:bg-emerald-700 transition-colors duration-200"
                >
                  {loading ? "Updating..." : "Save"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="w-3/5 relative h-screen">
        <img
          src={loginPlaceholder}
          alt="network-error"
          className="w-full h-full object-cover object-center"
        />
      </div>
    </div>
  );
};

export default ForgotPassword;
